import React, { useEffect, useState } from 'react';

import axios from 'axios';
import _, { cloneDeep, get, set } from 'lodash';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import {
  array, boolean,
  number,
  object, string,
} from 'yup';

import { faSync } from '@fortawesome/free-solid-svg-icons/faSync';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';

import { env } from '~/commons/config';

import Alert from '~/commons/components/Alert.jsx';
import Checkbox from '~/commons/components/Checkbox.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { FieldErpStatus, erpStatusHelper } from './FieldErpStatus.jsx';
import FormResource from './FormResource.jsx';

import { useAsyncResource } from '../../../commons/hooks/useAsyncResource';
import {
  FieldAddresses,
  FieldBasics,
  FieldExtraCards,
  FieldExtraItemsCards,
  FieldPaymentCards,
  FieldPayments,
  FieldProductsList,
  // TablePdfRecord,
  FormConvertBtn,
  ReportRibbon,
} from './orders';
import {
  arrDefaults,
  countryObj,
  extraDateTimeMap,
  extraSubmit,
  formDefaults,
  removeEmpty,
} from './orders/commons';
import FieldRestockProductsList from './orders/FieldRestockProductsList.jsx';
import FieldTfyProductsList from './orders/FieldTfyProductsList.jsx';

const requiredWhenCreate = string().when('refCode', {
  is: (refCode) => refCode === '',
  then: string().required(),
  otherwise: string(),
});

const schema = object({
  refCode: string(),
  status: string().required().oneOf(env.status.orders),
  currency: string()
    .required()
    .oneOf(env.currencies.map((v) => v.code)),
  orgId: number(),
  customer: object({
    erpId: string().nullable(),
  }).nullable(),
  contactErpId: requiredWhenCreate,
  billingAddress: object({
    erpId: requiredWhenCreate,
  }),
  extra: array().of(
    object({
      serviceType: string(),
    }),
  ),
  taxExempt: object({
    active: boolean(),
    reason: string().when('active', {
      is: (active) => active,
      then: string().required(),
      otherwise: string(),
    }),
  }),
  isTradePrice: boolean(),
  discount: object(),
  taxation: object(),
  subtotal: number(),
  total: number(),
});

const defaults = {
  ...formDefaults,
  cartType: 'order',
  erpId: '',
  contactErpId: '',
  prepaid: 0,
  exchangeRate: 0,
  paymentMethod: [arrDefaults.paymentMethod],
  taxExempt: {
    active: false,
    reason: '',
    number: '',
  },
  erpSync: {
    manualSync: false,
    lastSuccess: '',
    lastSync: '',
    errType: '',
    message: '',
  },
  discount: {},
  taxation: {},
  subtotal: 0,
  total: 0,
};

const FormBody = ({
  values: _values,
  setValues,
  order,
  infoWindow,
  setInfoWindow,
  showAlert,
  setShowAlert,
  api,
  sales,
  services,
  notFixedQty,
  stored,
  res,
  req,
}) => {
  const {
    resource: values, error, refresh, loading,
  } = useAsyncResource(async () => {
    if (order?.erpId) return _values;
    const { data } = (await axios.post(
      '/api/core/users/self/taxations/calculate',
      { ..._values, _id: get(stored, '_id') },
    )) || {};
    const taxObj = {
      'discount.total': data?.discount ?? 0,
      'taxation.total': data?.tax ?? 0,
      'taxation.calculated': data?.tax ?? 0,
      total: data?.total ?? 0,
      subtotal: data?.subtotal ?? 0,
    };
    const out = cloneDeep(_values);
    Object.entries(taxObj).forEach(([k, v]) => {
      set(out, k, v);
    });
    return out;
  }, [_values]);
  useEffect(() => { if (error) setShowAlert(true); }, [error]);

  const [lock, setLock] = useState();
  useEffect(() => {
    (async () => {
      setLock(get(stored, '_id'));
      if (!lock && get(stored, '_id')) return;

      const { data } = (await axios.post(
        '/api/core/users/self/taxations/calculateExtraFees',
        { ..._values, extra: [], _id: get(stored, '_id') },
      )) || {};
      const { extra, transportFeeServiceItemCode } = data ?? {};
      if (extra) {
        const transportFeeService = transportFeeServiceItemCode ? _.find(services, (x) => x.itemCode === transportFeeServiceItemCode) : null;
        setValues((v) => ({
          ...v,
          extra: _.map([
            ..._.filter(v.extra, (x) => x.service !== transportFeeService?._id),
            ...extra,
          ], (x) => ({
            ...x,
            service: _.isString(x.service) ? x.service : x.service?._id,
          })),
        }));
      }
    })();
  }, [_values?.items]);
  return (
    <>
      {order?.status === 'Booked' && values?.erpSync?.errType && (
        <FormConvertBtn
          res={res}
          icon={faSync}
          disabled={order?.erpId}
          onClick={() => req({
            url: `${api}/manualSync`,
            method: 'POST',
            data: { _id: order._id },
          })}>
          Sync
        </FormConvertBtn>
      )}

      <button
        className="btn btn-outline-primary ml-2 mb-3"
        onClick={(e) => {
          e.nativeEvent.preventDefault();
          refresh();
        }}
        type="button"
        disabled={loading}
      >
        <FA
          icon={loading ? faSpinner : faSync}
          fixedWidth
          pulse={loading}
        />
        Refresh
      </button>

      {infoWindow && (
        <Alert theme="info" onClose={() => setInfoWindow('')}>
          {JSON.stringify(infoWindow)}
        </Alert>
      )}
      {(error && showAlert) && (
        <Alert theme="danger" onClose={() => setShowAlert(false)}>
          <span>{error.message}</span>
        </Alert >
      )}
      {
        order && (
          <ReportRibbon
            api={api}
            refCode={order?.refCode}
            values={values}
            calTaxFn={() => { }}
          />
        )
      }

      <Tabs defaultActiveKey="order" unmountOnExit>
        <Tab eventKey="order" title="Order">
          <FieldBasics
            record={order}
            sales={sales}
            status={(env?.status?.orders || []).filter(
              (v) => v !== 'Closed',
            )}
            refCodeName='Sales order Number'
            disabled={!!order?.erpId}
          />
          <FormGroup
            name="contactErpId"
            label="Customer Contact ID"
            disabled={!!order?.erpId || !!order}
          />
          <FormGroup name="erpId" label="Order ERP ID" disabled />
          <FormGroup name="remarks" disabled={!!order?.erpId} />
          {order?.isTradePrice
            && <>
              <Checkbox value='true' className='checkbox-disabled' />
              <span className="text-muted small my-auto ml-3">
                Verified the customer's identity and confirm to proceed with the trade price
              </span>
            </>
          }
          <FieldAddresses formData={values} disabled={!!order?.erpId} />
        </Tab>
        <Tab eventKey="extra" title="Extra Services">
          <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
            Extra
          </h6>
          <FieldExtraCards
            services={services}
            notFixedQty={notFixedQty}
            disabled={!!order?.erpId}
          />
        </Tab>
        <Tab eventKey="payment" title="Payment">
          <FieldPayments order={order} values={values} disabled={!!order?.erpId} />

          <FormGroup name="prepaid" type="number" disabled />
          <FormGroup name="exchangeRate" type="number" disabled={!!order?.erpId} />
          <FieldPaymentCards />
        </Tab>
        <Tab eventKey="products" title="Products list">
          <div className="py-3">
            <FieldProductsList order={order} disabled={!!order?.erpId} />
          </div>
        </Tab>
        <Tab eventKey="restockProducts" title="Reorder Products list">
          <div className="py-3">
            <FieldRestockProductsList order={order} disabled={!!order?.erpId} />
          </div>
        </Tab>
        <Tab eventKey="tfyProducts" title="TFY Products list">
          <div className="py-3">
            <FieldTfyProductsList order={order} disabled={!!order?.erpId} />
          </div>
        </Tab>
        <Tab eventKey="extraItems" title="Custom Item">
          <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
            Custom Item
          </h6>
          <FieldExtraItemsCards disabled={!!order?.erpId} />
        </Tab>
        <Tab eventKey="erpStats" title="ERP Status">
          <FieldErpStatus />
        </Tab>
        {/* <Tab eventKey="pdfRecord" title="PDF Record">
        <TablePdfRecord
          target={order?._id}
          url="/api/core/orders/pdfRecord"
        />
      </Tab> */}
      </Tabs>
    </>
  );
};

const FormOrder = ({ order, services, sales = [] }) => {
  const api = '/api/core/orders';
  const [res, req] = useHttpStatus();
  const [infoWindow, setInfoWindow] = useState('');
  const [showAlert, setShowAlert] = useState(true);
  const notFixedQty = services
    .filter(({ fixedQty = true }) => !fixedQty)
    .reduce((a, v) => ({ ...a, [v._id]: v }), {});

  useEffect(() => {
    if (res.payload) {
      const mes = res.payload?.resPayload?.order?.record_status === 'S'
        ? 'Successfully Synchronized'
        : 'Failed to Synchronize';
      setInfoWindow(mes);
      console.log('res.payload', res.payload);
    }
  }, [res]);
  const stored = (order || undefined) && {
    ...order,
    extra: extraDateTimeMap(order.extra),
    erpSync: erpStatusHelper(order.erpSync),
  };

  return (
    <FormResource
      {...{
        defaults,
        schema,
        initialValuesOpts: {
          omitIds: false,
          transform: v => ({
            ...v,
            extra: _.map(v.extra, x => ({ ...x, service: x.service?._id })),
          }),
        },
        stored,
        api,
        onSubmit: (v) => {
          let data = cloneDeep(v);

          data = removeEmpty(data, ['extra', 'paymentMethod', 'extraItems']);
          data.extra = extraSubmit(data.extra, notFixedQty);
          console.log(data.extra);

          if (order) delete data.customer;
          if (!data.issuedBy) data.issuedBy = null;

          ['billingAddress', 'shippingAddress'].forEach((x) => {
            data[x].country = countryObj[data[x].countryCode] || '';
          });
          data.exchangeRate = parseFloat(data.exchangeRate) || 0;
          data.orgId = parseInt(data.orgId, 10) || undefined;
          data.items = data.items.map(({ _id }) => _id).filter((x) => !!x);
          return data;
        },
        onSubmitDone: (values, payload) => ({ ...values, extra: payload.extra }),
      }}>
      {({ values, setValues }) => <FormBody
        values={values}
        setValues={setValues}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        order={order}
        infoWindow={infoWindow}
        setInfoWindow={setInfoWindow}
        api={api}
        sales={sales}
        services={services}
        notFixedQty={notFixedQty}
        stored={stored}
        res={res}
        req={req}
      />
      }
    </FormResource>
  );
};

export default FormOrder;
